import { useRef, useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject, selectProjectThemePack, selectProjectThemeMode } from '@/store/slices/project/selectors';
import { campaignTemplatesMap } from '../../templates';
import { CampaignTemplate } from '@/connection/campaigns/fetch-campaign-templates';
import classes from '../create-campaign-flow.module.css';

const TEMPLATE_WIDTH = 520 + 80;

type PreviewSize = 'desktop' | 'tablet' | 'mobile';

interface TemplatePreviewProps {
  template: CampaignTemplate;
  previewSize: PreviewSize;
}

export default function TemplatePreview({ template, previewSize }: TemplatePreviewProps) {
  const event = useAppSelector(getWorkingProject);
  const theme = useAppSelector(selectProjectThemePack);
  const themeMode = useAppSelector(selectProjectThemeMode);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setScale(entry.contentRect.width / TEMPLATE_WIDTH);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const TemplateComponent = campaignTemplatesMap.get(template.uuid);

  return (
    <div className={classes.previewContainer}>
      <div className={`${classes.browserFrame} ${classes[`browserFrame--${previewSize}`]}`}>
        <div className={classes.browserChrome}>
          <span className={`${classes.trafficDot} ${classes.trafficDotRed}`} />
          <span className={`${classes.trafficDot} ${classes.trafficDotYellow}`} />
          <span className={`${classes.trafficDot} ${classes.trafficDotGreen}`} />
        </div>
        <div className={classes.emailClientLayout}>
          <div className={classes.emailSidebarNarrow}>
            <div className={classes.placeholderBar} style={{ width: '60%' }} />
            <div className={classes.placeholderBar} style={{ width: '40%' }} />
            <div className={classes.placeholderBar} style={{ width: '50%' }} />
            <div className={classes.placeholderBar} style={{ width: '70%' }} />
            <div className={classes.placeholderBar} style={{ width: '45%' }} />
            <div className={classes.placeholderBar} style={{ width: '55%' }} />
            <div className={classes.placeholderBar} style={{ width: '35%' }} />
            <div className={classes.placeholderBar} style={{ width: '65%' }} />
          </div>

          <div className={classes.emailSidebarWide}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={classes.emailListItem}>
                <div className={classes.placeholderDot} />
                <div className={classes.emailListItemLines}>
                  <div className={classes.placeholderBar} style={{ width: '80%' }} />
                  <div className={classes.placeholderBarLight} style={{ width: '60%' }} />
                </div>
              </div>
            ))}
          </div>

          <div className={classes.emailMain}>
            <div className={classes.emailHeader}>
              <div className={classes.emailHeaderRow}>
                <div className={classes.placeholderDot} />
                <div className={classes.emailHeaderLines}>
                  <div className={classes.placeholderBar} style={{ width: '50%' }} />
                  <div className={classes.placeholderBarLight} style={{ width: '30%' }} />
                </div>
              </div>
            </div>

            <div className={classes.emailBody}>
              <div ref={containerRef} style={{ width: '100%' }}>
                {event && theme && TemplateComponent && (
                  <div style={{ transform: `scale(${scale}` }}>
                    <div style={{ width: TEMPLATE_WIDTH }}>
                      <TemplateComponent event={event} theme={theme} mode={themeMode ?? undefined} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
